/**
 * FT-164: Hours of Rest Domain Hook - FLAT ARRAY VERSION
 *
 * Stores HOR data as a flat array like fleet_crew (NOT nested buckets).
 * This fixes cloud sync issues by using the proven array push path.
 *
 * Storage key: 'hours_of_rest' (org-scoped, no vessel suffix)
 * Storage format: [{id, crewId, date, workHours, restHours, workPeriods, ...}, ...]
 *
 * Signatures are stored as separate records in the same array with type: 'signature'
 */

import { devLog } from '../../utils/devLog';
import React, { useCallback, useMemo } from 'react';
import { useStorageState } from '../useStorageState';
import { STORAGE_KEYS } from '../../constants/STORAGE_KEYS';
import { usePermissions } from '../usePermissions';
import { v4 as uuidv4 } from 'uuid';
import { getOrgId } from '../../utils/getOrgId';  // FT-174: Stamp organizationId on records

// Lazy import to avoid circular deps
const getHoursOfRestRules = () => require('../../logic/regulations/hoursOfRest').hoursOfRestRules();

// --- HELPERS ---

// Generate deterministic ID for a HOR record
const idFor = (crewId, date) => `hor::${crewId}::${date}`;

// Clean periods array - filter out invalid entries
const cleanPeriods = (arr) =>
  Array.isArray(arr) ? arr.filter(p => p && p.start && p.end) : null;

// FT-174: Normalize a single record
// Preserve organizationId if present (don't overwrite with current org on read)
const normalize = (r) => {
  if (!r || typeof r !== 'object') return null;

  const crewId = r.crewId ?? r.crew_id ?? null;
  const date = r.date;

  // Generate ID if missing
  const id = r.id ?? (crewId && date ? idFor(crewId, date) : `hor_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);

  const normalized = {
    id,
    crewId,
    date,
    workHours: r.workHours ?? r.totalWork ?? 0,
    restHours: r.restHours ?? r.totalRest ?? 24,
    notes: r.notes ?? '',
    vesselId: r.vesselId,
    createdAt: r.createdAt ?? new Date().toISOString(),
    updatedAt: r.updatedAt ?? new Date().toISOString(),
    // FT-174: Preserve organizationId (don't overwrite with current org on read)
    organizationId: r.organizationId
  };

  // Handle workPeriods (WORK-based format)
  const workPeriods = cleanPeriods(r.workPeriods);
  if (workPeriods && workPeriods.length > 0) {
    normalized.workPeriods = workPeriods;
  }

  // Handle legacy periods (REST-based format)
  const periods = cleanPeriods(r.periods);
  if (periods && periods.length > 0) {
    normalized.periods = periods;
  } else if (r.workStart || r.workEnd) {
    if (r.workStart) normalized.workStart = r.workStart;
    if (r.workEnd) normalized.workEnd = r.workEnd;
  }

  return normalized;
};

// Get month key from date string (YYYY-MM)
const getMonthKey = (val) => {
  if (!val) return null;
  if (typeof val === 'string') {
    const m = /^(\d{4})-(\d{2})/.exec(val);
    if (m) return `${m[1]}-${m[2]}`;
  }
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

// Check if value is old bucket format
const isBucketsShape = (v) =>
  v && typeof v === 'object' && 'buckets' in v && 'signatures' in v;

// Check if value is legacy nested format: { crewId: { dateISO: {...} } }
const isLegacyNested = (obj) =>
  obj &&
  !Array.isArray(obj) &&
  typeof obj === 'object' &&
  !('buckets' in obj) &&
  !('signatures' in obj) &&
  Object.keys(obj).length > 0 &&
  Object.values(obj).every(v => v && typeof v === 'object' && !Array.isArray(v));

// Migrate bucket format to flat array
const migrateBucketsToFlat = (bucketData) => {
  const records = [];

  // Extract HOR records from buckets
  for (const [monthKey, bucket] of Object.entries(bucketData.buckets || {})) {
    if (Array.isArray(bucket)) {
      for (const rec of bucket) {
        const normalized = normalize(rec);
        if (normalized) records.push(normalized);
      }
    }
  }

  // Extract signatures and convert to records
  for (const [monthKey, sigs] of Object.entries(bucketData.signatures || {})) {
    for (const [role, sigData] of Object.entries(sigs || {})) {
      records.push({
        id: `sig::${monthKey}::${role}`,
        type: 'signature',
        monthKey,
        role,
        ...sigData,
        createdAt: sigData.signedAt || new Date().toISOString(),
        updatedAt: sigData.signedAt || new Date().toISOString()
      });
    }
  }

  return records;
};

// Migrate legacy nested format to flat array
const migrateLegacyNestedToFlat = (nested) => {
  const records = [];
  for (const [crewId, dates] of Object.entries(nested || {})) {
    if (!dates || typeof dates !== 'object') continue;
    for (const [date, hours] of Object.entries(dates)) {
      if (hours && typeof hours === 'object') {
        const normalized = normalize({ crewId, date, ...hours });
        if (normalized) records.push(normalized);
      }
    }
  }
  return records;
};

// Filter out wrapper/invalid records
const isValidRecord = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (item.data && item.data._isArray) return false;
  if (item.data && Array.isArray(item.data.items)) return false;
  return true;
};

// Normalize array from storage
const normalizeArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(isValidRecord);
};

// --- MAIN HOOK ---

export const useHoursOfRestData = () => {
  const { can, currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown User';

  // FT-164: Store as FLAT ARRAY (like fleet_crew)
  // noScope: true = org-scoped storage (no vessel suffix)
  const { value: raw = [], setValue } = useStorageState(STORAGE_KEYS.HOURS_OF_REST, [], { noScope: true });

  // One-time migration from bucket/legacy formats to flat array
  const migrationChecked = React.useRef(false);

  React.useEffect(() => {
    if (migrationChecked.current) return;
    migrationChecked.current = true;

    // Skip if already flat array
    if (Array.isArray(raw) && raw.length > 0 && !isBucketsShape(raw[0])) {
      devLog('[HOR FT-164] Already flat array format, no migration needed');
      return;
    }

    // Skip if empty/null
    if (!raw || (Array.isArray(raw) && raw.length === 0)) {
      devLog('[HOR FT-164] No existing data to migrate');
      return;
    }

    // Migrate bucket format to flat array
    if (isBucketsShape(raw)) {
      devLog('[HOR FT-164] Migrating bucket format to flat array');
      const flat = migrateBucketsToFlat(raw);
      if (flat.length > 0) {
        setValue(flat);
        devLog('[HOR FT-164] Migrated', flat.length, 'records to flat array');
      }
      return;
    }

    // Migrate legacy nested format to flat array
    if (isLegacyNested(raw)) {
      devLog('[HOR FT-164] Migrating legacy nested format to flat array');
      const flat = migrateLegacyNestedToFlat(raw);
      if (flat.length > 0) {
        setValue(flat);
        devLog('[HOR FT-164] Migrated', flat.length, 'records to flat array');
      }
      return;
    }
  }, []); // Run once on mount

  // FT-175: Normalized list (excluding signature records AND tombstoned records)
  // Records with deletedAt are soft-deleted (tombstones for sync)
  const list = useMemo(() => {
    const arr = normalizeArray(raw);
    return arr
      .filter(r => r.type !== 'signature' && !r.deletedAt)
      .map(normalize)
      .filter(Boolean);
  }, [raw]);

  // FT-175: Get all records including deleted (for admin/restore purposes)
  const listWithDeleted = useMemo(() => {
    const arr = normalizeArray(raw);
    return arr.filter(r => r.type !== 'signature').map(normalize).filter(Boolean);
  }, [raw]);

  // Signatures (extracted from flat array)
  const signatures = useMemo(() => {
    const arr = normalizeArray(raw);
    const sigMap = {};
    for (const r of arr) {
      if (r.type === 'signature' && r.monthKey && r.role) {
        if (!sigMap[r.monthKey]) sigMap[r.monthKey] = {};
        sigMap[r.monthKey][r.role] = {
          ...r,
          signedAt: r.signedAt || r.createdAt
        };
      }
    }
    return sigMap;
  }, [raw]);

  // FT-174: Add new HOR record - RBAC-gated
  // Always stamp organizationId to prevent cross-org contamination
  const save = useCallback((input = {}) => {
    if (!can('create', 'hours_of_rest') && !can('update', 'hours_of_rest')) {
      devLog('[HOR] Permission denied: create/update');
      return null;
    }

    const now = new Date().toISOString();
    const currentOrgId = getOrgId();
    const crewId = input.crewId ?? input.crew_id;
    const date = input.date;

    if (!crewId || !date) {
      devLog('[HOR] Cannot save: missing crewId or date');
      return null;
    }

    const id = input.id || idFor(crewId, date);
    const record = {
      ...normalize(input),
      id,
      crewId,
      date,
      updatedAt: now,
      updatedBy: userName,
      // FT-174: Organization stamp (prevents cross-org contamination)
      organizationId: input.organizationId || currentOrgId
    };

    setValue(prev => {
      const current = normalizeArray(prev);
      const existingIdx = current.findIndex(r => r.id === id || (r.crewId === crewId && r.date === date));

      if (existingIdx >= 0) {
        // Update existing
        const updated = [...current];
        updated[existingIdx] = { ...current[existingIdx], ...record };
        return updated;
      } else {
        // Add new
        return [...current, { ...record, createdAt: now }];
      }
    });

    devLog('[HOR] Saved record:', id);
    return record;
  }, [can, setValue, userName]);

  // FT-174: Batch save multiple records
  // FT-164: Now handles BOTH flat array records AND legacy nested format {crewId: {date: {...}}}
  // Always stamp organizationId to prevent cross-org contamination
  const saveBatch = useCallback((inputs = []) => {
    if (!can('create', 'hours_of_rest') && !can('update', 'hours_of_rest')) {
      return { success: false, error: 'Permission denied', saved: 0, ids: [], records: [] };
    }

    // FT-164: Detect and convert legacy nested format to flat array
    let records;
    if (Array.isArray(inputs)) {
      records = inputs;
    } else if (inputs && typeof inputs === 'object' && !inputs.crewId && !inputs.date) {
      // Legacy nested format: {crewId: {date: {...}, date2: {...}}, crewId2: {...}}
      records = [];
      for (const [crewId, dates] of Object.entries(inputs)) {
        if (!dates || typeof dates !== 'object') continue;
        for (const [date, data] of Object.entries(dates)) {
          if (data && typeof data === 'object') {
            records.push({ crewId, date, ...data });
          }
        }
      }
      devLog('[HOR FT-164] Converted nested format to', records.length, 'flat records');
    } else {
      // Single flat record
      records = [inputs];
    }

    const validRecords = records.filter(r => r && r.crewId && r.date);

    if (validRecords.length === 0) {
      return { success: true, saved: 0, ids: [], records: [] };
    }

    const now = new Date().toISOString();
    const currentOrgId = getOrgId();
    const savedRecords = [];
    const savedIds = [];

    setValue(prev => {
      const current = normalizeArray(prev);
      const updated = [...current];

      for (const input of validRecords) {
        const crewId = input.crewId ?? input.crew_id;
        const date = input.date;
        const id = input.id || idFor(crewId, date);

        const record = {
          ...normalize(input),
          id,
          crewId,
          date,
          updatedAt: now,
          updatedBy: userName,
          // FT-174: Organization stamp (prevents cross-org contamination)
          organizationId: input.organizationId || currentOrgId
        };

        const existingIdx = updated.findIndex(r => r.id === id || (r.crewId === crewId && r.date === date));

        if (existingIdx >= 0) {
          updated[existingIdx] = { ...updated[existingIdx], ...record };
        } else {
          updated.push({ ...record, createdAt: now });
        }

        savedRecords.push(record);
        savedIds.push(id);
      }

      return updated;
    });

    devLog(`[HOR] Batch saved ${savedRecords.length} records`);
    return { success: true, saved: savedRecords.length, ids: savedIds, records: savedRecords };
  }, [can, setValue, userName]);

  // FT-175: Remove record - now uses tombstone pattern
  // Records are marked with deletedAt instead of being removed from array
  // This allows sync to propagate deletions properly
  // @param id - ID of the record to delete
  // @param hardDelete - if true, marks for permanent deletion (no restore)
  const remove = useCallback((id, hardDelete = false) => {
    if (!can('delete', 'hours_of_rest')) {
      devLog('[HOR] Permission denied: delete');
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeArray(prev);
      return current.map(r => r.id === id
        ? {
            ...r,
            deletedAt: now,
            deletedBy: userName,
            _hardDelete: hardDelete,  // Flag for future cloud cleanup
            updatedAt: now,
            updatedBy: userName
          }
        : r
      );
    });

    devLog(`[HOR] FT-175: Tombstoned record ${id} (hardDelete: ${hardDelete})`);
    return true;
  }, [can, setValue, userName]);

  // FT-175: Restore record - clears tombstone fields
  const restore = useCallback((id) => {
    if (!can('update', 'hours_of_rest')) {
      devLog('[HOR] Permission denied: update');
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeArray(prev);
      return current.map(r => r.id === id
        ? {
            ...r,
            // FT-175: Clear tombstone fields on restore
            deletedAt: null,
            deletedBy: null,
            _hardDelete: false,
            updatedAt: now,
            updatedBy: userName
          }
        : r
      );
    });

    devLog(`[HOR] FT-175: Restored record ${id}`);
    return true;
  }, [can, setValue, userName]);

  // FT-174: Sign a month - RBAC-gated
  // Always stamp organizationId to prevent cross-org contamination
  const sign = useCallback((monthKey, role, signature) => {
    if (!can('sign', 'hours_of_rest')) {
      throw new Error('Permission denied: Cannot sign hours of rest');
    }

    // Normalize role
    const normalizedRole = String(role || '').toLowerCase().trim();
    const roleMap = { 'skipper': 'master', 'captain': 'master', 'master': 'master', 'owner': 'owner' };
    const finalRole = roleMap[normalizedRole] || normalizedRole;

    if (!['master', 'owner'].includes(finalRole)) {
      throw new Error('Invalid role: must be master or owner');
    }

    const now = new Date().toISOString();
    const currentOrgId = getOrgId();
    const sigId = `sig::${monthKey}::${finalRole}`;

    setValue(prev => {
      const current = normalizeArray(prev);
      // Remove existing signature for this month/role
      const filtered = current.filter(r => r.id !== sigId);
      // Add new signature record
      return [...filtered, {
        id: sigId,
        type: 'signature',
        monthKey,
        role: finalRole,
        ...signature,
        signedAt: signature.signedAt || now,
        createdAt: now,
        updatedAt: now,
        // FT-174: Organization stamp (prevents cross-org contamination)
        organizationId: currentOrgId
      }];
    });

    devLog('[HOR] Signed month:', monthKey, finalRole);
    return { monthKey, role: finalRole, signedAt: now };
  }, [can, setValue]);

  // Get records for a specific month
  const getForMonth = useCallback((monthKey) => {
    return list.filter(r => getMonthKey(r.date) === monthKey);
  }, [list]);

  // Get records for a specific crew member
  const getForCrew = useCallback((crewId) => {
    return list.filter(r => r.crewId === crewId);
  }, [list]);

  // Get single record by ID
  const getById = useCallback((id) => {
    return list.find(r => r.id === id) || null;
  }, [list]);

  // Get record for specific crew + date
  const getForCrewDate = useCallback((crewId, date) => {
    return list.find(r => r.crewId === crewId && r.date === date) || null;
  }, [list]);

  // Get signatures for a month
  const getSignatures = useCallback((monthKey) => {
    return signatures[monthKey] || {};
  }, [signatures]);

  // Get compliance for a month
  const getCompliance = useCallback((monthKey) => {
    const records = getForMonth(monthKey);
    const rules = getHoursOfRestRules();
    return rules.checkMonth(monthKey, records);
  }, [getForMonth]);

  // FT-175: Get deleted (tombstoned) records for admin/restore view
  const getDeleted = useCallback(() => {
    return listWithDeleted.filter(r => r.deletedAt);
  }, [listWithDeleted]);

  // Get statistics
  const getStats = useCallback(() => {
    return {
      total: list.length,
      byCrew: list.reduce((map, r) => {
        const crewId = r.crewId || 'unknown';
        map[crewId] = (map[crewId] || 0) + 1;
        return map;
      }, {}),
      avgWorkHours: list.length > 0
        ? list.reduce((sum, r) => sum + (r.workHours || 0), 0) / list.length
        : 0,
      avgRestHours: list.length > 0
        ? list.reduce((sum, r) => sum + (r.restHours || 0), 0) / list.length
        : 0,
      monthsWithSignatures: Object.keys(signatures).length,
      monthsWithData: new Set(list.map(r => getMonthKey(r.date)).filter(Boolean)).size
    };
  }, [list, signatures]);

  // Return memoized interface
  return useMemo(() => ({
    // Data - flat array for UI
    data: list,
    dataWithDeleted: listWithDeleted,  // FT-175: All records including tombstoned (for admin)

    // Query helpers
    getForMonth,
    getForCrew,
    getForCrewDate,
    getById,
    getDeleted,      // FT-175: Get tombstoned records
    getSignatures,
    getCompliance,
    getStats,

    // CRUD operations (RBAC-gated)
    save,
    saveBatch,
    remove,
    restore,         // FT-175: Restore tombstoned records
    sign,

    // Utilities
    normalize,

    // Permission checks
    canEdit: () => can('update', 'hours_of_rest'),
    canDelete: () => can('delete', 'hours_of_rest'),
    canSign: () => can('sign', 'hours_of_rest'),

    // Computed values
    totalRecords: list.length
  }), [list, listWithDeleted, getForMonth, getForCrew, getForCrewDate, getById, getDeleted, getSignatures, getCompliance, getStats, save, saveBatch, remove, restore, sign, can]);
};

export default useHoursOfRestData;
